// The artist studio's test copy (Section D). Manager, the Hub and the Store run here exactly as they are, but every
// request for data is answered from made-up data (fixtures.json) inside the browser, so nothing real can show up and
// nothing can be changed. Browser storage is a throwaway copy too, so the real sign-in on this computer is never
// read or touched. It turns on only inside the studio's preview frame (window name "3dl-studio-sandbox", which stays
// set while the frame moves between pages) or with ?studio-sandbox=1 in the address.
// On top of that it swaps in the artist's uploads (icons, pictures, tile animations, backgrounds, theme), shows the
// "before" or "after" when the studio asks, and rings the exact spot an item is used.
// The made-up data loads only inside the test copy, so the real pages never download it.
let fixtures = { get: {} };
let fixturesReady = null;
const loadFixtures = () => (fixturesReady ||= import('./fixtures.json').then((m) => { fixtures = m.default || m; return fixtures; }));
// Enough of a made-up person for the first paint; the full one comes from the made-up data a moment later.
const DEMO_USER = { id: 1, full_name: 'Demo Owner', initials: 'DO', role: 'Administrator', is_owner: true, active: true, preferences: { theme: 'dark' }, permissions: {}, two_factor_enabled: true };

export const SANDBOX_NAME = '3dl-studio-sandbox';
const params = new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
export const sandboxActive = typeof window !== 'undefined' && (window.name === SANDBOX_NAME || params.get('studio-sandbox') === '1');

// Paths the test copy may really ask the server: only the studio's own uploads (they need the studio link's cookie).
const PASS = /^\/api\/studio\/(overrides|files\/\d+)(\?|$)/;

function memoryStorage(seed = {}) {
  const map = new Map(Object.entries(seed));
  return {
    get length() { return map.size; },
    key: (i) => [...map.keys()][i] ?? null,
    getItem: (k) => (map.has(String(k)) ? map.get(String(k)) : null),
    setItem: (k, v) => { map.set(String(k), String(v)); },
    removeItem: (k) => { map.delete(String(k)); },
    clear: () => map.clear(),
  };
}

// The made-up answer for a request. Reads come from fixtures.json; anything else gets an empty answer, and every
// change is accepted and forgotten.
// ?studio-state=empty shows every list empty (for the empty-list pictures); ?studio-state=loading keeps the page
// waiting forever (for the loading animations).
const STATE = ['empty', 'loading', 'boot'].includes(params.get('studio-state')) ? params.get('studio-state') : '';
const KEEP = new Set(['/me', '/bootstrap', '/setup/status', '/ping', '/store/session', '/hub/layout', '/services/status', '/visitor-stats/status', '/manager-usage/me', '/drive/google-sync']);
export const emptied = (value) => (Array.isArray(value) ? [] : value && typeof value === 'object' ? Object.fromEntries(Object.entries(value).map(([k, v]) => [k, Array.isArray(v) ? [] : v && typeof v === 'object' && !['settings', 'site', 'homepage', 'payment'].includes(k) ? emptied(v) : v])) : value);
const WAIT_KEEP = new Set(['/setup/status', '/ping']);
export function fakeAnswer(method, pathname, data = fixtures) {
  const route = pathname.replace(/^\/api/, '').replace(/\/+$/, '') || '/';
  const get = data.get || {};
  if (method !== 'GET' && method !== 'HEAD') {
    if (route === '/auth/media-pass') return { status: 200, body: { pass: 'sandbox', expires_at: new Date(Date.now() + 3600e3).toISOString() } };
    if (route === '/hub/usage' || route.startsWith('/manager-usage')) return { status: 204, body: null };
    return { status: 200, body: { ok: true, sandbox: true } };
  }
  // "Empty" state (for empty-list pictures): every list is empty, but the made-up person and settings stay.
  if (STATE === 'empty' && !KEEP.has(route)) {
    if (Object.hasOwn(get, route)) return { status: 200, body: emptied(get[route]) };
    return { status: 200, body: [] };
  }
  if (Object.hasOwn(get, route)) return { status: 200, body: get[route] };
  if (route === '/ping') return { status: 200, body: { ok: true } };
  const item = /^\/(customers|jobs|tasks|store\/products)\/(\d+)$/.exec(route);
  if (item) { const list = get[`/${item[1]}`]; const found = Array.isArray(list) ? list.find((r) => String(r.id) === item[2]) : null; return found ? { status: 200, body: found } : { status: 404, body: { error: 'Not in the test copy.' } }; }
  if (/^\/public\/store\/products\/[^/]+$/.test(route)) { const slug = route.split('/').pop(); const p = (get['/public/store']?.products || []).find((x) => x.slug === slug); return p ? { status: 200, body: { product: p } } : { status: 404, body: { error: 'Not in the test copy.' } }; }
  if (route === '/public/store/products') return { status: 200, body: { products: get['/public/store']?.products || [] } };
  return { status: 200, body: [] };
}

function install() {
  window.name = SANDBOX_NAME;
  window.__3DL_SANDBOX__ = true;
  // Storage: a fresh, made-up sign-in that lives only in this page.
  const fakeLocal = memoryStorage({ '3dlabs.sessionToken': 'sandbox', '3dlabs.currentUser': JSON.stringify(DEMO_USER) });
  loadFixtures();
  try { Object.defineProperty(window, 'localStorage', { configurable: true, get: () => fakeLocal }); } catch { /* leave as is */ }
  try { const fakeSession = memoryStorage(); Object.defineProperty(window, 'sessionStorage', { configurable: true, get: () => fakeSession }); } catch { /* leave as is */ }

  const realFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = new URL(typeof input === 'string' ? input : input.url, location.href);
    if (url.origin !== location.origin || !url.pathname.startsWith('/api/')) return realFetch(input, init);
    if (PASS.test(url.pathname + url.search)) return realFetch(url.href, { credentials: 'same-origin' });
    const method = String(init.method || (typeof input === 'object' && input.method) || 'GET').toUpperCase();
    await loadFixtures().catch(() => null);
    const route = url.pathname.replace(/^\/api/, '').replace(/\/+$/, '');
    // Loading states: the page's own data never arrives ("boot": not even the sign-in, for the start-up screen).
    if (method === 'GET' && ((STATE === 'loading' && !KEEP.has(route) && !WAIT_KEEP.has(route)) || (STATE === 'boot' && !WAIT_KEEP.has(route)))) return new Promise(() => {});
    const answer = fakeAnswer(method, url.pathname);
    return new Response(answer.body === null ? null : JSON.stringify(answer.body), { status: answer.status, headers: { 'content-type': 'application/json' } });
  };
  // Older request styles never reach the server either.
  const RealXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = class extends RealXHR {
    open(method, url, ...rest) { const u = new URL(url, location.href); this.__fake = u.origin === location.origin && u.pathname.startsWith('/api/') && !PASS.test(u.pathname); return super.open(method, this.__fake ? 'data:application/json,%7B%22ok%22%3Atrue%2C%22sandbox%22%3Atrue%7D' : url, ...rest); }
    setRequestHeader(...args) { if (!this.__fake) super.setRequestHeader(...args); }
  };
  window.EventSource = class { constructor() { this.readyState = 0; } addEventListener() {} removeEventListener() {} close() {} };
  if (navigator.serviceWorker) try { Object.defineProperty(navigator.serviceWorker, 'register', { configurable: true, value: () => Promise.resolve(null) }); } catch { /* fine */ }
  try { navigator.sendBeacon = () => true; } catch { /* fine */ }

  // Links inside the copy stay inside the copy.
  document.addEventListener('click', (event) => {
    const a = event.target.closest?.('a[href]');
    if (!a) return;
    const u = new URL(a.href, location.href);
    if (u.origin !== location.origin) { event.preventDefault(); return; }
    if (!u.searchParams.has('studio-sandbox')) { u.searchParams.set('studio-sandbox', '1'); a.href = u.href; }
    a.removeAttribute('target');
  }, true);

  // A strip so nobody mistakes it for the real thing.
  const strip = () => {
    if (params.get('studio-thumb') === '1' || document.getElementById('studio-sandbox-strip')) return;
    const el = document.createElement('div');
    el.id = 'studio-sandbox-strip';
    el.textContent = 'Test copy · made-up data';
    el.style.cssText = 'position:fixed;left:50%;bottom:8px;transform:translateX(-50%);z-index:2147483646;padding:4px 12px;border-radius:999px;background:rgba(255,179,71,.92);color:#1a1200;font:700 12px/1.4 system-ui,sans-serif;pointer-events:none;letter-spacing:.02em';
    document.body.appendChild(el);
  };
  if (document.body) strip(); else document.addEventListener('DOMContentLoaded', strip);

  startOverrides();
}

// ---------- The artist's uploads ----------
let overrides = { items: [], theme: null };
let mode = params.get('studio-view') === 'before' ? 'before' : 'after';
let svgCache = new Map();
const STYLE_ID = 'studio-sandbox-overrides';

async function loadOverrides() {
  try {
    const r = await window.fetch('/api/studio/overrides');
    if (r.ok) overrides = await r.json();
  } catch { /* the copy still works without them */ }
  apply();
}
async function svgText(url) {
  if (!svgCache.has(url)) svgCache.set(url, window.fetch(url).then((r) => (r.ok ? r.text() : '')).catch(() => ''));
  return svgCache.get(url);
}
function cssFor(item) {
  const url = JSON.stringify(item.url);
  if (!item.selector) return '';
  switch (item.kind) {
    case 'background': return `${item.selector}{background-image:url(${url})!important;background-size:cover!important;background-position:center!important}`;
    case 'store-banner': return `${item.selector}{background-image:url(${url})!important;background-size:cover!important;background-position:center!important}`;
    case 'badge': return `${item.selector}{background-image:url(${url})!important;background-size:contain!important;background-repeat:no-repeat!important;background-position:center!important}`;
    case 'empty-state': case 'loader': return `${item.selector}::before{content:"";display:block;width:min(240px,60%);aspect-ratio:4/3;margin:0 auto 12px;background:url(${url}) center/contain no-repeat}`;
    default: return '';
  }
}
function themeCss(theme) {
  if (!theme?.colors) return '';
  const c = theme.colors, fonts = theme.fonts || {};
  const faces = ['heading', 'body'].filter((slot) => fonts[slot]?.upload).map((slot) => `@font-face{font-family:"${fonts[slot].family}";src:url("/api/studio/files/${fonts[slot].upload}");font-display:swap}`).join('');
  const body = fonts.body?.family ? `body,.app,.shop,button,input{font-family:"${fonts.body.family}",system-ui,sans-serif!important}` : '';
  const heading = fonts.heading?.family ? `h1,h2,h3,strong{font-family:"${fonts.heading.family}",system-ui,sans-serif!important}` : '';
  return `${faces}:root,.app,.hub-app,.shop{--accent:${c.brand}!important;--custom-bg:${c.background}!important;--studio-accent2:${c.accent};--studio-good:${c.good};--studio-warn:${c.warn};--studio-bad:${c.bad};--studio-surface:${c.surface};--studio-text:${c.text};--studio-text-soft:${c.textSoft}}.hub-app,.hub2{background:${c.oled}!important}${body}${heading}`;
}
async function swapElement(el, item) {
  if (el.dataset.studioDone === String(item.url)) return;
  if (item.placeholder?.type === 'lucide') {
    if (!el.dataset.studioBefore) el.dataset.studioBefore = el.innerHTML, el.dataset.studioBeforeBox = el.getAttribute('viewBox') || '0 0 24 24';
    if (item.format === 'SVG') {
      const doc = new DOMParser().parseFromString(await svgText(item.url), 'image/svg+xml').documentElement;
      if (doc?.nodeName !== 'svg') return;
      el.setAttribute('viewBox', doc.getAttribute('viewBox') || `0 0 ${doc.getAttribute('width') || 24} ${doc.getAttribute('height') || 24}`);
      el.innerHTML = doc.innerHTML;
    } else { el.setAttribute('viewBox', '0 0 24 24'); el.innerHTML = `<image href="${item.url}" x="0" y="0" width="24" height="24" preserveAspectRatio="xMidYMid meet"/>`; }
  } else if (item.placeholder?.type === 'file' && el.tagName === 'IMG') {
    if (!el.dataset.studioBefore) el.dataset.studioBefore = el.getAttribute('src');
    el.setAttribute('src', item.url);
  } else if (item.placeholder?.type === 'hub-art') {
    el.style.visibility = 'hidden';
    let sib = el.nextElementSibling;
    if (!sib?.classList?.contains('studio-art')) {
      sib = document.createElement(item.format === 'MP4' ? 'video' : 'img');
      sib.className = `${el.getAttribute('class')} studio-art`;
      Object.assign(sib.style, { objectFit: 'contain', objectPosition: 'right bottom' });
      if (sib.tagName === 'VIDEO') Object.assign(sib, { muted: true, loop: true, autoplay: true, playsInline: true });
      el.after(sib);
    }
    sib.src = item.url;
  }
  el.dataset.studioDone = String(item.url);
}
function restore() {
  document.getElementById(STYLE_ID)?.remove();
  for (const el of document.querySelectorAll('[data-studio-done]')) {
    if (el.dataset.studioBefore !== undefined) {
      if (el.tagName === 'IMG') el.setAttribute('src', el.dataset.studioBefore);
      else { el.innerHTML = el.dataset.studioBefore; el.setAttribute('viewBox', el.dataset.studioBeforeBox || '0 0 24 24'); }
    }
    el.style.visibility = '';
    if (el.nextElementSibling?.classList?.contains('studio-art')) el.nextElementSibling.remove();
    delete el.dataset.studioDone; delete el.dataset.studioBefore; delete el.dataset.studioBeforeBox;
  }
}
function apply() {
  if (mode === 'before') { restore(); return; }
  let style = document.getElementById(STYLE_ID);
  if (!style) { style = document.createElement('style'); style.id = STYLE_ID; document.head.appendChild(style); }
  const css = overrides.items.map(cssFor).join('\n') + themeCss(overrides.theme);
  if (style.textContent !== css) style.textContent = css;
  for (const item of overrides.items) {
    if (!['lucide', 'file', 'hub-art'].includes(item.placeholder?.type) || !item.selector) continue;
    for (const el of document.querySelectorAll(item.selector)) if (!el.classList.contains('studio-art')) swapElement(el, item);
  }
}

// ---------- The spot ----------
let ring = null, ringTrack = 0;
// What the ring goes around: the element itself, except a Hub tile's animation, where it's the whole tile (the
// animation sits in the tile's bottom-right corner, so a ring around it looked shifted and too small).
export function ringTarget(el) {
  if (el?.matches?.('.hub2-art')) return el.closest('.hub2-tile') || el;
  return el;
}
// Brings the element to the middle of the copy's own scroll boxes and window. Never scrollIntoView: that also scrolls
// every box around the frame up to the studio's own page, which shoved the whole studio sideways (Matt, 10-26).
export function centerInPage(el) {
  for (let box = el.parentElement; box && box !== document.documentElement; box = box.parentElement) {
    const st = getComputedStyle(box);
    const sx = box.scrollWidth > box.clientWidth && !['visible', 'clip'].includes(st.overflowX);
    const sy = box.scrollHeight > box.clientHeight && !['visible', 'clip'].includes(st.overflowY);
    if (!sx && !sy) continue;
    const b = box.getBoundingClientRect(), r = el.getBoundingClientRect();
    box.scrollBy({ behavior: 'instant',
      left: sx ? r.left + r.width / 2 - (b.left + box.clientLeft + box.clientWidth / 2) : 0,
      top: sy ? r.top + r.height / 2 - (b.top + box.clientTop + box.clientHeight / 2) : 0 });
  }
  const r = el.getBoundingClientRect();
  window.scrollBy({ behavior: 'instant', left: r.left + r.width / 2 - innerWidth / 2, top: r.top + r.height / 2 - innerHeight / 2 });
}
// Tells the studio around the frame (if any) how the spot went, so it can show the copy only once it has settled.
const tellStudio = (message) => { if (window.parent !== window) window.parent.postMessage(message, location.origin); };
// Waits until the page has settled (fonts loaded, the element not moving for a few frames: the Hub's layout,
// images), brings the element into view, then draws the ring exactly on its border box and keeps it there every
// frame while the page scrolls, resizes or moves.
async function showRing(target, selector = null) {
  cancelAnimationFrame(ringTrack);
  ring?.remove();
  document.querySelectorAll('[data-studio-target]').forEach((x) => x.removeAttribute('data-studio-target'));
  try { await document.fonts?.ready; } catch { /* fine */ }
  const frame = () => new Promise((r) => requestAnimationFrame(r));
  let last = '', still = 0;
  for (let i = 0; i < 180 && still < 8; i += 1) { await frame(); const r = target.getBoundingClientRect(); const key = [r.left, r.top, r.width, r.height].map(Math.round).join(); still = key === last ? still + 1 : 0; last = key; }
  centerInPage(target);
  target.setAttribute('data-studio-target', '1');
  ring = document.createElement('div');
  ring.id = 'studio-sandbox-ring';
  const B = 3; // the border sits just outside the element, so its inside edge is the element's edge
  Object.assign(ring.style, { position: 'fixed', boxSizing: 'border-box', border: `${B}px solid #ffb347`, borderRadius: '10px', boxShadow: '0 0 0 4000px rgba(0,0,0,.35), 0 0 18px #ffb347', transition: 'box-shadow .8s ease', zIndex: 2147483645, pointerEvents: 'none' });
  document.body.appendChild(ring);
  const mine = ring;
  const place = () => {
    if (ring !== mine || !document.body.contains(mine)) return;
    // The page may draw the element again (an easter egg popping up again): follow the new one.
    if (!target.isConnected && selector) { let again = null; try { again = [...document.querySelectorAll(selector)].find((x) => { const b = x.getBoundingClientRect(); return b.width > 0 && b.height > 0; }); } catch { again = null; } if (again) { target = ringTarget(again); target.setAttribute('data-studio-target', '1'); } }
    const r = target.getBoundingClientRect();
    Object.assign(mine.style, { left: `${r.left - B}px`, top: `${r.top - B}px`, width: `${r.width + 2 * B}px`, height: `${r.height + 2 * B}px` });
    ringTrack = requestAnimationFrame(place);
  };
  place();
  // A few soft pulses around the ring catch the eye on a small spot (the outline doesn't change the ring's box).
  try { mine.animate([{ outline: '2px solid rgba(255,179,71,.9)', outlineOffset: '0px' }, { outline: '2px solid rgba(255,179,71,0)', outlineOffset: '14px' }], { duration: 900, iterations: 3, delay: 250, easing: 'ease-out' }); } catch { /* fine */ }
  tellStudio({ type: 'studio-spot-ready', selector });
  setTimeout(() => { if (ring === mine) mine.style.boxShadow = '0 0 0 4000px rgba(0,0,0,0), 0 0 18px #ffb347'; }, 2500);
}
function highlight(selector) {
  if (!selector) return;
  let tries = 0;
  const find = () => {
    let el = null;
    // The first one that is really drawn (some matches are hidden or zero-sized).
    try { el = [...document.querySelectorAll(selector)].find((x) => { const r = x.getBoundingClientRect(); return r.width > 0 && r.height > 0 && !x.classList.contains('studio-art'); }); } catch { return; }
    if (!el) {
      if ((tries += 1) < 40) setTimeout(find, 250);
      else if (params.get('studio-thumb') === '1') window.parent.postMessage({ type: 'studio-thumb-rect', key: params.get('studio-key'), missing: true }, location.origin);
      else tellStudio({ type: 'studio-spot-missing', selector });
      return;
    }
    // Thumbnail mode (a small live crop on a studio card): no ring, no strip; tell the card where the element is.
    if (params.get('studio-thumb') === '1') {
      document.getElementById('studio-sandbox-strip')?.remove();
      document.documentElement.style.overflow = 'hidden';
      centerInPage(el);
      const send = () => { const r = el.getBoundingClientRect(); window.parent.postMessage({ type: 'studio-thumb-rect', key: params.get('studio-key'), rect: { x: r.left, y: r.top, width: r.width, height: r.height }, view: { width: innerWidth, height: innerHeight } }, location.origin); };
      send(); setTimeout(send, 600); setTimeout(send, 2000);
      return;
    }
    showRing(ringTarget(el), selector);
  };
  find();
}

function startOverrides() {
  const spot = params.get('studio-spot');
  let selector = null;
  try { selector = spot ? atob(spot.replace(/-/g, '+').replace(/_/g, '/')) : null; } catch { selector = null; }
  const go = () => {
    loadOverrides();
    if (selector) highlight(selector);
    // #open-first: open the first record on the page (a customer's own page), where some visuals only appear.
    if (location.hash === '#open-first') { let n = 0; const t = setInterval(() => { const first = document.querySelector('.directory-grid > *'); if (first) { first.click(); clearInterval(t); } else if ((n += 1) > 40) clearInterval(t); }, 250); }
    // An easter egg item: show that egg now and again, so it can be seen and compared.
    const egg = params.get('egg');
    if (/^[a-z0-9-]{2,40}$/.test(egg || '')) { const fire = () => window.dispatchEvent(new CustomEvent('3dlabs-easter-test', { detail: { slot: egg } })); setTimeout(fire, 1500); setInterval(() => { if (!document.querySelector('.easter-overlay')) fire(); }, 500); } // popped up again as soon as it hides, never twice at once
    new MutationObserver(() => { if (mode === 'after' && overrides.items.length) apply(); }).observe(document.body, { childList: true, subtree: true });
    setInterval(loadOverrides, 20e3);
  };
  if (document.body) go(); else document.addEventListener('DOMContentLoaded', go);
  // The studio talks to the copy: before/after, a new upload, or a new spot to ring.
  window.addEventListener('message', (event) => {
    if (event.origin !== location.origin || !event.data || typeof event.data !== 'object') return;
    if (event.data.type === 'studio-view') { mode = event.data.mode === 'before' ? 'before' : 'after'; restore(); apply(); }
    if (event.data.type === 'studio-refresh') { svgCache = new Map(); restore(); loadOverrides(); }
    if (event.data.type === 'studio-highlight') highlight(String(event.data.selector || ''));
  });
}

if (sandboxActive) install();
